import { Note } from "@domain/purchasing/models/Notes";
import { Stack, Typography, Tooltip, Chip, Box, IconButton, Button, useTheme, Menu, MenuItem, Avatar, Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText } from "@mui/material";
import { useState } from "react";
import { format } from "date-fns";
import { RichTextField } from "@shared/ui/common/RichTextField";
import { Icon } from "@shared/icons";
import { PurchaseOrderItem } from "@domain/purchasing";
import { Delete, DeleteOutline } from "@mui/icons-material";

interface NoteBlockProps {
    note: Note,
    items: PurchaseOrderItem[],
    onUpdateNote: (note: Note) => void
    onDeleteNote: (note: Note) => void
}

export const NoteBlock: React.FC<NoteBlockProps> = ({ note, items, onUpdateNote, onDeleteNote }) => {

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const theme = useTheme();
    const [isEditing, setIsEditing] = useState(false);
    const [noteContent, setNoteContent] = useState(note.content);
    const [notePurchaseOrderItem, setNotePurchaseOrderItem] = useState<PurchaseOrderItem | undefined>(note.purchaseOrderItem);

    const handleUpdateNote = () => {
        onUpdateNote({ ...note, content: noteContent, purchaseOrderItem: notePurchaseOrderItem });
        setIsEditing(false);
    }

    const handleDeleteClick = () => {
        setShowDeleteConfirm(true);
    }

    const handleDeleteConfirm = () => {
        onDeleteNote(note);
        setShowDeleteConfirm(false);
    }

    const handleDeleteCancel = () => {
        setShowDeleteConfirm(false);
    }

    if (isEditing) {
        return (

            <Stack direction="row" gap={2}>

                <Avatar
                    sx={{ width: 32, height: 32, border: 1, borderColor: 'divider' }}
                />


                <Stack gap={1} flex={1}>

                    <Stack direction="row" alignItems="center" justifyContent="flex-start" gap={2}>
                        <Typography variant="caption" color="text.secondary" noWrap>
                            {note.createdAt && format(note.createdAt, 'MMM d, yyyy hh:mm a')}
                        </Typography>

                        {notePurchaseOrderItem ? (
                            <Tooltip title={notePurchaseOrderItem.product.title}>
                                <Chip
                                    size="small"
                                    icon={<Icon icon="LinkBroken" />}
                                    onClick={() => { setNotePurchaseOrderItem(undefined); setIsMenuOpen(false) }}
                                    label={
                                        <Box sx={{
                                            textOverflow: 'ellipsis',
                                            overflow: 'hidden',
                                        }}>
                                            {notePurchaseOrderItem.product.title}
                                        </Box>
                                    } />
                            </Tooltip>
                        ) : items.length > 0 && (
                            <Chip label="Link item" onClick={(event) => { setAnchorEl(event.currentTarget); setIsMenuOpen(true) }} />
                        )}

                    </Stack>



                    <RichTextField
                        value={noteContent}
                        onChange={(value) => setNoteContent(value)}
                        placeholder="Add notes about this purchase order..."
                    />

                    <Stack direction="row" alignItems="flex-end" justifyContent="flex-end" gap={2}>
                        <Stack direction="row" gap={2}>
                            <Button variant="text" size="small" color="secondary" onClick={() => setIsEditing(false)}>Cancel </Button>
                            <Button variant="contained" size="small" onClick={handleUpdateNote} disabled={note.content === ''} > Save  </Button>
                        </Stack>

                        <Menu anchorEl={anchorEl} open={isMenuOpen} onClose={() => setIsMenuOpen(false)}>
                            {items.map((item) => (
                                <MenuItem
                                    key={item.id}
                                    color="secondary"
                                    onClick={() => { setNotePurchaseOrderItem(item); setIsMenuOpen(false) }}
                                    sx={{
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    <Box sx={{
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}>{item.product.title}</Box>
                                </MenuItem>
                            ))}
                        </Menu>

                    </Stack>
                </Stack>
            </Stack>
        )
    }

    return (
        <>
            <Stack direction="row" gap={2}>

                <Avatar
                    sx={{ width: 32, height: 32, border: 1, borderColor: 'divider' }}
                />

                <Stack direction="column" justifyContent="space-between" alignItems="flex-start" gap={0} flex={1}>

                    <Stack direction="row" gap={1} alignItems="center">
                        <Typography variant="caption" color="text.secondary" noWrap>
                            {note.createdAt && format(note.createdAt, 'MMM d, yyyy hh:mm a')}
                        </Typography>

                        {notePurchaseOrderItem ? (
                            <Tooltip title={notePurchaseOrderItem.product.title}>
                                <Chip
                                    size="small"
                                    label={
                                        <Box sx={{
                                            textOverflow: 'ellipsis',
                                            overflow: 'hidden',
                                            maxWidth: '100%',
                                        }}>
                                            {notePurchaseOrderItem.product.title}
                                        </Box>
                                    } />
                            </Tooltip>
                        ) : (
                            <Chip size="small" label="No item linked" />
                        )}

                    </Stack>


                    <Typography variant="body2" dangerouslySetInnerHTML={{ __html: note.content }} />


                    <Stack direction="row" justifyContent="flex-start" alignItems="center" width="100%" pb={1} gap={1}>

                        <IconButton size="small" sx={{ border: 1, borderColor: 'divider', borderRadius: '4px' }} onClick={() => setIsEditing(true)}>
                            <Icon icon="IcEdit" />
                        </IconButton>

                        <IconButton size="small" sx={{ border: 1, borderColor: 'divider', borderRadius: '4px' }} onClick={handleDeleteClick}>
                            <DeleteOutline color="error" fontSize="small" />
                        </IconButton>

                    </Stack>


                </Stack>
            </Stack>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={showDeleteConfirm}
                onClose={handleDeleteCancel}
                aria-labelledby="delete-note-dialog-title"
                aria-describedby="delete-note-dialog-description"
            >
                <DialogTitle id="delete-note-dialog-title">
                    Delete Note
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="delete-note-dialog-description">
                        Are you sure you want to delete this note? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={handleDeleteConfirm} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}